import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Redirect, useParams } from "react-router-dom";

import Container from "@material-ui/core/Container";
import Divider from "@material-ui/core/Divider";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";

import LinearProgress from "@material-ui/core/LinearProgress";
import Paper from "@material-ui/core/Paper";
import Snackbar from "@material-ui/core/Snackbar";

import { useAlertMessage } from "../../../shared/hooks/useAlertMessage";
import { RootState } from "../../../store";
import { useStepper } from "../../hooks/useStepper";
import { AssessmentQuestion } from "../assessmentQuestion";
import { AssessmentStepper } from "../assessmentStepper";

import { Answer, Template } from "../../../models/api/assessment";
import { Request } from "../../../models/api/request";

import AssessmentCompletedBanner from "../assessmentCompletedBanner/assessmentCompletedBanner";

import { resetRequest } from "../../../shared/request/actions/requestActions";
import {
  asyncFinishParticipantAssessment,
  asyncGetParticipantAssessment,
  asyncSubmitParticipantAnswer,
  redirectToParticipant,
  resetRedirection,
} from "../../actions";

import useStyles from "./assessment-form.styles";

const PENDING_ASSESSMENT_QUESTIONS_MESSAGE =
  "You have not answered all the questions.";

const INITIAL_ASSESSMENT_PATH = "assessments/questions";

const DEFAULT_QUESTION = null;
const DEFAULT_ANSWER_ID = 0;

interface Redirect {
  isExternalURL: boolean;
  isRedirect: boolean;
  redirectTo: string;
}

interface Selector {
  request: Request;
  redirection: Redirect;
  assessmentTemplate: Template[];
  participantAnswers: Answer[];
}

export const AssessmentForm = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [currentQuestion, setCurrentQuestion] = useState<Template | null>(
    DEFAULT_QUESTION
  );
  const [currentAnswerId, setCurrentAnswerId] =
    useState<number>(DEFAULT_ANSWER_ID);
  const [questionsTotal, setQuestionsTotal] = useState<number>(0);
  const [selectedAnswerId, setSelectedAnswerId] =
    useState<number>(DEFAULT_ANSWER_ID);
  const { id: participantId } = useParams<{ id: string }>();
  const { isOpen, openAlertMesssage, handleClose } = useAlertMessage();

  const { request, redirection, assessmentTemplate, participantAnswers } =
    useSelector<RootState, Selector>((state) => ({
      request: state.request.request,
      assessmentTemplate:
        state.initialAssessment.initialAssessment.assessmentTemplate,
      participantAnswers:
        state.initialAssessment.initialAssessment.participantAnswers,
      redirection: state.initialAssessment.initialAssessment.redirection,
    }));

  const {
    steps,
    activeStep,
    setActiveStep,
    handleNext: handleStepperNext,
    handleBack: handleStepperBack,
    updateTotalSteps,
  } = useStepper(0);

  const submitAnswer = () => {
    if (
      selectedAnswerId === DEFAULT_ANSWER_ID ||
      currentAnswerId === selectedAnswerId
    )
      return;

    dispatch(
      asyncSubmitParticipantAnswer({
        questionId: currentQuestion.questionId,
        answerId: selectedAnswerId,
        participantProfileId: participantId,
      })
    );
  };

  const setCurrentQuestionData = useCallback(
    (step) => {
      const selectedQuestion = assessmentTemplate[step]
        ? { ...assessmentTemplate[step] }
        : null;
      setCurrentQuestion(selectedQuestion);
      const selectedAnswer = selectedQuestion
        ? participantAnswers.find(
            (question) => question.questionId === selectedQuestion.questionId
          )
        : undefined;
      const updateSelectedAnswerId =
        selectedAnswer === undefined
          ? DEFAULT_ANSWER_ID
          : selectedAnswer.answerId;
      setCurrentAnswerId(updateSelectedAnswerId);
      setSelectedAnswerId(updateSelectedAnswerId);
    },
    [assessmentTemplate, participantAnswers]
  );

  const handleBack = () => {
    submitAnswer();
    if (activeStep > 0) setCurrentQuestionData(activeStep - 1);
    handleStepperBack();
  };

  const handleNext = () => {
    submitAnswer();
    setCurrentQuestionData(activeStep + 1);
    handleStepperNext();
  };

  const redirectToQuestion = useCallback(
    (step) => {
      setActiveStep(step);
      setCurrentQuestionData(step);
    },
    [setActiveStep, setCurrentQuestionData]
  );

  const validateAssessment = useCallback(() => {
    let pendingQuestionIndex = -1;

    for (let index = 0; index < assessmentTemplate.length; index += 1) {
      const question = assessmentTemplate[index];
      const completedAnswerIndex = participantAnswers.findIndex(
        (answer) => answer.questionId === question.questionId
      );
      if (completedAnswerIndex === -1) {
        pendingQuestionIndex = index;
        break;
      }
    }

    return pendingQuestionIndex;
  }, [assessmentTemplate, participantAnswers]);

  const handleComplete = () => {
    const missingQuestionIndex = validateAssessment();

    if (missingQuestionIndex !== -1) {
      redirectToQuestion(missingQuestionIndex);
      openAlertMesssage();
      return;
    }

    dispatch(asyncFinishParticipantAssessment(participantId));
  };

  const goToCompleteAssessmentButton = useCallback(() => {
    setCurrentQuestionData(assessmentTemplate.length);
    setActiveStep(assessmentTemplate.length);
  }, [assessmentTemplate.length, setActiveStep, setCurrentQuestionData]);

  const stepperProps = {
    steps,
    activeStep,
    handleNext,
    handleBack,
    handleComplete,
  };

  const handleAlertClose = () => {
    dispatch(resetRequest());
  };

  const handleSelectedAnswer = (answerId: number) => {
    setSelectedAnswerId(answerId);
  };

  useEffect(() => {
    updateTotalSteps(assessmentTemplate.length);
    if (
      assessmentTemplate.length !== questionsTotal &&
      assessmentTemplate.length > 0
    ) {
      setQuestionsTotal(assessmentTemplate.length);
      const firstMissingQuestionIndex = validateAssessment();
      if (firstMissingQuestionIndex !== -1)
        redirectToQuestion(firstMissingQuestionIndex);
      else goToCompleteAssessmentButton();
    }
  }, [
    assessmentTemplate.length,
    goToCompleteAssessmentButton,
    questionsTotal,
    redirectToQuestion,
    updateTotalSteps,
    validateAssessment,
  ]);

  useEffect(() => {
    if (request.type === "success") {
      setTimeout(() => {
        redirectToParticipant("/participant/dashboard");
      }, 1000);
    }
  }, [request.type, dispatch, participantId]);

  useEffect(() => {
    dispatch(resetRedirection());
    dispatch(asyncGetParticipantAssessment(participantId));
  }, [dispatch, participantId]);

  if (redirection.isRedirect) {
    if (redirection.isExternalURL)
      redirectToParticipant(redirection.redirectTo);
    if (!redirection.redirectTo.includes(INITIAL_ASSESSMENT_PATH)) {
      return <Redirect to={redirection.redirectTo} />;
    }
  }

  return (
    <>
      <Container className={classes.assessmentContainer}>
        <Grid item lg={12} md={12} sm={12} xs={12}>
          <Paper className={classes.paper}>
            <img
              className={classes.ribbonImage}
              src="/ribbon.svg"
              alt="Mentoro assessment"
            />
            {request.isFetching && (
              <div className={classes.root}>
                <LinearProgress color="secondary" />
              </div>
            )}
            <Grid container>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Typography className={classes.title} variant="h5" align="left">
                  Financial Progress Check
                </Typography>
                <Divider className={classes.divider} />
              </Grid>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                {currentQuestion ? (
                  <AssessmentQuestion
                    question={currentQuestion}
                    onChange={handleSelectedAnswer}
                    selectedAnswerId={selectedAnswerId}
                  />
                ) : (
                  <AssessmentCompletedBanner />
                )}
              </Grid>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                {activeStep > -1 && <Divider />}
              </Grid>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                {activeStep > -1 && <AssessmentStepper {...stepperProps} />}
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Container>
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={request.isOpen}
        autoHideDuration={6000}
        onClose={handleAlertClose}
        message={request.message}
      />
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={isOpen}
        onClose={handleClose}
        message={PENDING_ASSESSMENT_QUESTIONS_MESSAGE}
      />
    </>
  );
};
